﻿import { Dispatch, SetStateAction, useContext, useEffect, useRef } from 'react';

import { StudentData, StudentDetailsContext } from 'pages/students/context/StudentsDefaultContext';
import { Certificates } from 'pages/students/components/Certificates/Certificates';
import { Transactions } from 'pages/students/components/Transactions/Transactions';
import { Activity } from 'pages/students/components/Activity/Activity';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { TileContainer } from 'components/TileContainer/TileContainer';
import { GMetrixTabs, GMetrixTabsTab } from 'components/GMetrixTabs';
import { Details } from 'pages/students/components/Details/Details';
import { Groups } from 'pages/students/components/Groups/Groups';
import { Transcript } from 'components/Transcript/Transcript';
import { savePDF } from '@progress/kendo-react-pdf';
import { FormattedMessage } from 'react-intl';
import { buildExportFileName } from 'utils';
import { Tab } from 'components/Tabs/Tabs';
import { Student } from 'apis/StudentApi';
import { useStickyTabIndex } from 'hooks';
import { useJoyride } from 'app/joyride';

interface StudentProfileProps extends Tab<StudentData> {
    Data: any;
    Id: number;
    setStudents: Dispatch<SetStateAction<Student[]>>;
    Title: string;
}

export const StudentProfile = (props: StudentProfileProps) => {

    const { Data, Id, setStudents, Title } = props;

    const { openTabIndex, pageContext, setSubTabIndex, subTabIndex } = useContext(StudentDetailsContext);
    const { loadCurrentTour } = useJoyride();

    // Remember the active sub-tab per student across refreshes.
    useStickyTabIndex({
        entityId: pageContext?.Id,
        onRestore: index => {

            const copyOfSubTabIndex = [...subTabIndex];
            copyOfSubTabIndex[openTabIndex - 1] = index;
            setSubTabIndex(copyOfSubTabIndex);

        },
        scope: 'student',
        value: subTabIndex[openTabIndex - 1] ?? 0
    });

    const contentRef = useRef<HTMLDivElement>(null);

    const printElement = () => {

        const element = document.getElementById('transcriptPDF') as HTMLElement;
        savePDF(element, {
            fileName: buildExportFileName('Lookup', pageContext?.Title ? `Transcript ${pageContext?.Title}` : 'Transcript', 'pdf'),
            margin: 40,
            paperSize: 'auto'
        });

    };

    useEffect(() => {

        loadCurrentTour('ContentTab');

    }, []);

    return (
        <GMetrixTabs
            unmountOnExit
            onSelect={i => {

                const copyOfSubTabIndex = [...subTabIndex];
                copyOfSubTabIndex[openTabIndex - 1] = i;
                setSubTabIndex(copyOfSubTabIndex);

            }}
            selected={subTabIndex[openTabIndex - 1]}
        >
            <GMetrixTabsTab
                title={(
                    <div className="joyride-details-tab">
                        <FormattedMessage defaultMessage="Details" description="Gridview header" id="details.65c6087a" />
                    </div>
                )}
            >
                <Details
                    setStudents={setStudents}
                    studentId={Id}
                    title={Title}
                    {...Data}
                />
            </GMetrixTabsTab>
            <GMetrixTabsTab
                title={(
                    <div className="joyride-classrooms-tab">
                        <FormattedMessage defaultMessage="Classrooms" id="classrooms.e66138fb" />
                    </div>
                )}
            >
                <Groups studentId={Id} {...Data} />
            </GMetrixTabsTab>
            <GMetrixTabsTab
                title={(
                    <div className="joyride-accessCodes-tab">
                        <FormattedMessage defaultMessage="Access Codes" id="access_codes.3114ae04" />
                    </div>
                )}
            >
                <Transactions studentId={Id} {...Data} />
            </GMetrixTabsTab>
            <GMetrixTabsTab
                title={(
                    <div className="joyride-activity-tab">
                        <FormattedMessage defaultMessage="Reports and Activity" id="reports_and_activity.a8f1343a" />
                    </div>
                )}
            >
                <Activity studentId={Id} />
            </GMetrixTabsTab>
            {pageContext?.Id && (
                <GMetrixTabsTab
                    title={(
                        <div className="joyride-details-tab">
                            <FormattedMessage defaultMessage="Transcript" id="transcript.7697900a" />
                        </div>
                    )}
                >
                    <TileContainer
                        addButton
                        addButtonCustom={(
                            <GMetrixButton iconClass="fa-solid fa-print" onClick={printElement} variant="primary">
                                <FormattedMessage defaultMessage="Print" id="print.1fd3b13a" />
                            </GMetrixButton>
                        )}
                    >
                        <div id="transcriptPDF" ref={contentRef}>
                            <Transcript
                                student={pageContext?.Data.studentWithDetails}
                                studentId={pageContext?.Id}
                            />
                        </div>
                    </TileContainer>
                </GMetrixTabsTab>
            )}
            <GMetrixTabsTab
                title={(
                    <div className="joyride-certificates-tab">

                        <FormattedMessage defaultMessage="Certificates" id="certificates.03accec8" />
                    </div>
                )}
            >
                <Certificates context="student" id={pageContext?.Id} />
            </GMetrixTabsTab>
        </GMetrixTabs>
    );

};
